<template>
  <canvas ref="canvas"></canvas>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref } from 'vue';
import fragmentShaderSource from './fragment';

const canvas = ref();
const gl = ref();
const shaderProgram = ref();

const themeColors = ref({
  lineColor: [1, 0.458, 0.094, 1],      // fallback: #FF7518
  bgColor0: [0.133, 0.133, 0.133, 1],   // fallback: #222222
  bgColor1: [0.243, 0.129, 0.302, 1],   // fallback: #3E1A4D
});

function cssVarToRgba(varName, fallback = '255,117,24,1') {
  const style = getComputedStyle(document.documentElement);
  let value = style.getPropertyValue(varName).trim();
  if (!value) value = fallback;
  const parts = value.split(',').map(Number);
  while (parts.length < 4) parts.push(1);
  return parts.map((v, i) => i < 3 ? v / 255 : v);
}

onMounted(() => {
  gl.value = canvas.value?.getContext('webgl');
  if (!gl.value) {
    console.error('WebGL not supported');
    return;
  }
  themeColors.value.lineColor = cssVarToRgba('--v-theme-primary', '255,117,24,1');
  themeColors.value.bgColor0 = cssVarToRgba('--v-theme-surface-primary', '34,34,34,1');
  themeColors.value.bgColor1 = cssVarToRgba('--v-theme-surface-primary-high', '62,33,77,1');
  setupShaders();
  animate();
});

const stopAnimation = ref(false);
onBeforeUnmount(() => {
  stopAnimation.value = true;
});

function setupShaders() {
  const vertexShaderSource = `
    attribute vec4 aVertexPosition;
    void main(void) {
      gl_Position = aVertexPosition;
    }
  `;

  const compileShader = (source, type) => {
    const shader = gl.value.createShader(type);
    gl.value.shaderSource(shader, source);
    gl.value.compileShader(shader);
    if (!gl.value.getShaderParameter(shader, gl.value.COMPILE_STATUS)) {
      console.error(gl.value.getShaderInfoLog(shader));
      gl.value.deleteShader(shader);
      return null;
    }
    return shader;
  };

  const vertexShader = compileShader(vertexShaderSource, gl.value.VERTEX_SHADER);
  const fragmentShader = compileShader(fragmentShaderSource, gl.value.FRAGMENT_SHADER);

  shaderProgram.value = createShaderProgram(vertexShader, fragmentShader);
  gl.value.useProgram(shaderProgram.value);

  initBuffers();
}

function createShaderProgram(vertex, fragment) {
  const program = gl.value.createProgram();
  gl.value.attachShader(program, vertex);
  gl.value.attachShader(program, fragment);
  gl.value.linkProgram(program);
  if (!gl.value.getProgramParameter(program, gl.value.LINK_STATUS)) {
    console.error(gl.value.getProgramInfoLog(program));
    return null;
  }
  return program;
}

function initBuffers() {
  const vertices = new Float32Array([
    -1.0, -1.0,
    1.0, -1.0,
    1.0, 1.0,
    -1.0, 1.0,
  ]);

  const indices = new Uint16Array([0, 1, 2, 0, 2, 3]);

  const vertexBuffer = gl.value.createBuffer();
  gl.value.bindBuffer(gl.value.ARRAY_BUFFER, vertexBuffer);
  gl.value.bufferData(gl.value.ARRAY_BUFFER, vertices, gl.value.STATIC_DRAW);

  const indexBuffer = gl.value.createBuffer();
  gl.value.bindBuffer(gl.value.ELEMENT_ARRAY_BUFFER, indexBuffer);
  gl.value.bufferData(gl.value.ELEMENT_ARRAY_BUFFER, indices, gl.value.STATIC_DRAW);

  const aVertexPosition = gl.value.getAttribLocation(shaderProgram.value, 'aVertexPosition');
  gl.value.enableVertexAttribArray(aVertexPosition);
  gl.value.vertexAttribPointer(aVertexPosition, 2, gl.value.FLOAT, false, 0, 0);
}

function resizeCanvas() {
  const displayWidth = canvas.value.clientWidth;
  const displayHeight = canvas.value.clientHeight;
  if (canvas.value.width !== displayWidth || canvas.value.height !== displayHeight) {
    canvas.value.width = displayWidth;
    canvas.value.height = displayHeight;
    gl.value.viewport(0, 0, gl.value.drawingBufferWidth, gl.value.drawingBufferHeight);
  }
};

function animate() {
  const iTime = gl.value.getUniformLocation(shaderProgram.value, 'iTime');
  const iResolution = gl.value.getUniformLocation(shaderProgram.value, 'iResolution');
  const uLineColor = gl.value.getUniformLocation(shaderProgram.value, 'uLineColor');
  const uBgColor0 = gl.value.getUniformLocation(shaderProgram.value, 'uBgColor0');
  const uBgColor1 = gl.value.getUniformLocation(shaderProgram.value, 'uBgColor1');

  const loop = (time) => {
    if (stopAnimation.value) return;

    resizeCanvas();
    gl.value.clear(gl.value.COLOR_BUFFER_BIT);

    gl.value.uniform1f(iTime, time * 0.001);
    gl.value.uniform2f(iResolution, canvas.value?.width, canvas.value?.height);
    gl.value.uniform4fv(uLineColor, themeColors.value.lineColor);
    gl.value.uniform4fv(uBgColor0, themeColors.value.bgColor0);
    gl.value.uniform4fv(uBgColor1, themeColors.value.bgColor1);

    gl.value.drawElements(gl.value.TRIANGLES, 6, gl.value.UNSIGNED_SHORT, 0);
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);
}
</script>

<style scoped lang="scss">
  canvas {
    border: 1px solid black;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1;
  }
</style>
